import type { ParamSpec } from "@/api/types";
import { parsePineStrategyProps } from "@/lib/strategy-leverage";

/**
 * Engine settings for a new backtest. With the server's reading of the
 * script (`suggested`, from strategy.info) they are exactly what TradingView
 * would use for it: what strategy() declares, and Pine's defaults for the rest
 * (1,000,000 capital, 1 contract, no commission, 1x leverage, fills at the
 * next bar's open).
 *
 * Without it (the server couldn't say) they fall back to TradingView's
 * defaults with a 0.1% commission, and the few values read from the source.
 */
export const TV_INITIAL_CAPITAL = 1_000_000;
export const DEFAULT_COMMISSION_PCT = 0.1;

export type EngineKind = "spot" | "margin";
export type ValueSource = "script" | "tradingview" | "ours" | "engine";

export function newBacktestEngineConfig(
  kind: EngineKind,
  specs: ParamSpec[],
  source: string,
  suggested?: Record<string, unknown> | null
): { config: Record<string, unknown>; from: Record<string, ValueSource> } {
  const config: Record<string, unknown> = {};
  const from: Record<string, ValueSource> = {};
  for (const s of specs) {
    if (s.default !== undefined && s.default !== null) {
      config[s.name] = s.default;
      from[s.name] = "engine";
    }
  }
  const has = (name: string) => specs.length === 0 || specs.some((s) => s.name === name);
  const set = (name: string, value: unknown, src: ValueSource) => {
    if (!has(name)) return;
    config[name] = value;
    from[name] = src;
  };

  const capitalKey = kind === "spot" ? "fiat" : "initial";
  set(capitalKey, TV_INITIAL_CAPITAL, "tradingview");
  set("fee", DEFAULT_COMMISSION_PCT, "ours");
  if (kind === "margin") {
    set("pyramiding", 0, "tradingview");
    set("margin_long", 100, "tradingview");
    set("margin_short", 100, "tradingview");
  }

  const props = parsePineStrategyProps(source);
  if (props.initial != null) set(capitalKey, props.initial, "script");
  if (props.fee != null) set("fee", props.fee, "script");
  if (kind === "margin") {
    if (props.margin_long != null) set("margin_long", props.margin_long, "script");
    if (props.margin_short != null) set("margin_short", props.margin_short, "script");
  }
  if (suggested) for (const [name, value] of Object.entries(suggested)) set(name, value, "script");
  return { config, from };
}

/** True when the script declares strategy(…); indicators compile but place no trades. */
export function declaresStrategy(source: string): boolean {
  return /(^|[^.\w])strategy\s*\(/m.test(source);
}

/** Name from `strategy("…")`, or null when the script has none. */
export function pineStrategyName(source: string): string | null {
  const m = source.match(/\bstrategy\s*\(\s*(?:title\s*=\s*)?["']([^"']+)["']/);
  return m?.[1]?.trim() || null;
}

export type HistoryPreset = "3M" | "6M" | "1Y" | "YTD" | "custom";

const PRESET_MONTHS: Record<"3M" | "6M" | "1Y", number> = { "3M": 3, "6M": 6, "1Y": 12 };

/**
 * Start of the range as "YYYY-MM-DD 00:00:00" (UTC), counted back from `now`.
 * "YTD" is the current year: from 1 January to now.
 */
export function historyStart(preset: Exclude<HistoryPreset, "custom">, now = new Date()): string {
  const d =
    preset === "YTD"
      ? new Date(Date.UTC(now.getUTCFullYear(), 0, 1))
      : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - PRESET_MONTHS[preset], now.getUTCDate()));
  return `${d.toISOString().slice(0, 10)} 00:00:00`;
}

/** Chip labels for the history presets, in display order. */
export const HISTORY_PRESETS: { id: HistoryPreset; label: string }[] = [
  { id: "3M", label: "3M" },
  { id: "6M", label: "6M" },
  { id: "1Y", label: "1Y" },
  { id: "YTD", label: "Current year" },
  { id: "custom", label: "Custom" },
];

/** Timeframe like "15m", "4h", "1d", "1w" in minutes; null when unknown. */
export function timeframeMinutes(tf: string): number | null {
  const m = tf.trim().match(/^(\d+)\s*([mhdwM])$/);
  if (!m) return null;
  const n = Number(m[1]);
  const unit = { m: 1, h: 60, d: 1440, w: 10080, M: 43200 }[m[2] as "m" | "h" | "d" | "w" | "M"];
  return n * unit;
}

/** Rough candle count between start and end (or now). */
export function estimateCandles(start: string, end: string | null, tf: string, now = new Date()): number | null {
  const mins = timeframeMinutes(tf);
  const from = Date.parse(start.replace(" ", "T") + (start.endsWith("Z") ? "" : "Z"));
  const to = end ? Date.parse(end.replace(" ", "T") + (end.endsWith("Z") ? "" : "Z")) : now.getTime();
  if (!mins || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  return Math.floor((to - from) / 60000 / mins);
}

/** The script's own name: no market, since the user may change it later. */
export function defaultBacktestName(strategy: string | null): string {
  return strategy?.trim() || "Backtest";
}

export const EXAMPLE_PINE = `//@version=6
strategy("EMA Crossover", overlay=true, initial_capital=10000, default_qty_type=strategy.percent_of_equity, default_qty_value=100)

fastLen = input.int(21, "Fast length")
slowLen = input.int(55, "Slow length")

fast = ta.ema(close, fastLen)
slow = ta.ema(close, slowLen)
plot(fast, color=color.teal)
plot(slow, color=color.orange)

if ta.crossover(fast, slow)
    strategy.entry("Long", strategy.long)
if ta.crossunder(fast, slow)
    strategy.close("Long")
`;

